import {
  Calendar, Building, User, CreditCard, Globe, Mail, Phone, MapPin,
  Shield, FileExclamationPoint, QrCode, Wallet, ListCheck, ScrollText, Users
} from 'lucide-react';
import { useState } from 'react';

// Composants de base réutilisables
export const InfoItem = ({ label, value, icon, config }) => {
  const mergedConfig = { ...config };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      fontFamily: mergedConfig.fontFamily
    }}>
      <div style={{
        fontSize: mergedConfig.fontSize * 0.9,
        color: mergedConfig.primaryColor, // Titres en primary color
        fontWeight: 500
      }}>
        {label}
      </div>
      <div style={{
        fontSize: mergedConfig.fontSize,
        color: mergedConfig.textColor || '#1f2937', // Valeurs en text color
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        gap: 6
      }}>
        {icon} {value}
      </div>
    </div>
  );
};

// Composants de section spécifiques
export const HeaderSection = ({ data, config, t, logoConfig }) => {
  const hasLogo = logoConfig?.header?.enabled && logoConfig?.logoUrl;
  const position = logoConfig?.header?.position || 'left';

  return (
    <div className='pdf-section'>
      <div style={{
        marginBottom: config.gap,
        background: config.sectionColor, // Utilise la couleur de fond principale
        paddingBottom: 24,
        borderBottom: `2px solid ${config.sectionColor}30`, // Bordure avec section color
        position: 'relative'
      }}>
        {hasLogo && position !== 'center' && (
          <div style={{
            position: 'absolute',
            top: 0,
            [position]: 0,
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            zIndex: 1
          }}>
            <img
              src={logoConfig.logoUrl}
              alt={t('companyLogo') || 'Logo'}
              style={{
                width: LOGO_SIZES.find(s => s.value === logoConfig.header?.size)?.size || 100,
                height: 'auto',
                maxWidth: '100%'
              }}
            />
          </div>
        )}

        <div style={{
          textAlign: 'center',
          marginLeft: hasLogo && position === 'left' ?
            (LOGO_SIZES.find(s => s.value === logoConfig.header?.size)?.size || 100) + 20 : 0,
          marginRight: hasLogo && position === 'right' ?
            (LOGO_SIZES.find(s => s.value === logoConfig.header?.size)?.size || 100) + 20 : 0
        }}>
          {hasLogo && position === 'center' && (
            <div style={{ marginBottom: 16 }}>
              <img
                src={logoConfig.logoUrl}
                alt={t('companyLogo') || 'Logo'}
                style={{
                  width: LOGO_SIZES.find(s => s.value === logoConfig.header?.size)?.size || 100,
                  height: 'auto',
                  maxWidth: '100%',
                  margin: '0 auto'
                }}
              />
            </div>
          )}

          <h1 style={{
            fontSize: `calc(${config.fontSize || 32}px * 2)`,
            fontWeight: 'bold',
            color: config.primaryColor, // Titre principal en primary color
            margin: '0 0 8px 0',
            letterSpacing: '2px'
          }}>
            {data.title || t('extranetVoucher')}
          </h1>

          <div style={{
            fontSize: `calc(${config.fontSize || 18}px * 1.5)`,
            color: config.secondaryColor, // Sous-titre en secondary color
            marginBottom: 16,
            fontWeight: 500
          }}>
            {t('bookingReference')}: {data.bookingNumber}
          </div>

          <div style={{
            display: 'inline-block',
            padding: '8px 24px',
            borderRadius: '20px',
            fontSize: config.fontSize,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            background: data.statusColor,
            color: 'white'
          }}>
            {data.status}
          </div>
        </div>
      </div>
    </div>
  );
};

export const BookingInfoSection = ({ data, config, t, getServiceIcon }) => (
  <div className='pdf-section'>
    <div style={{
      marginBottom: config.gap,
      fontFamily: config.fontFamily,
      background: config.sectionColor, // Fond de section avec section color
      padding: '20px',
      borderRadius: '8px',
      border: `1px solid ${config.sectionColor}40`
    }}>
      <div style={{
        fontSize: `calc(${config.fontSize || 18}px * 1.5)`,
        fontWeight: 'bold',
        color: config.primaryColor, // Titre de section en primary color
        marginBottom: 20,
        paddingBottom: 8,
        borderBottom: `2px solid ${config.primaryColor}`,
        textTransform: 'uppercase',
        letterSpacing: '1px',
        display: 'flex',
        alignItems: 'center',
        gap: 8
      }}>
        <Calendar size={20} />
        {t('bookingInformation')}
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: config.columns > 1 ? `repeat(${config.columns}, 1fr)` : '1fr',
        gap: 20
      }}>
        <InfoItem
          config={config}
          label={t('serviceType')}
          value={t(data.serviceType)}
          icon={getServiceIcon ? getServiceIcon(data.serviceType) : <Building size={16} />}
        />
        <InfoItem
          config={config}
          label={t('checkinDate')}
          value={data.checkin}
          icon={<Calendar size={16} />}
        />
        <InfoItem
          config={config}
          label={t('checkoutDate')}
          value={data.checkout}
          icon={<Calendar size={16} />}
        />
        <InfoItem
          config={config}
          label={t('totalAmount')}
          value={`${data.totalAmount} ${data.currency}`}
          icon={<Wallet size={16} />}
        />
        <InfoItem
          config={config}
          label={t('paymentStatus')}
          value={t(data.paymentStatus?.toLowerCase()) || data.paymentStatus}
          icon={<CreditCard size={16} />}
        />
        <InfoItem
          config={config}
          label={t('bookingDate')}
          value={data.bookingDate}
          icon={<Calendar size={16} />}
        />
      </div>
    </div>
  </div>
);

export const HolderSection = ({ data, config, t, formatDate }) => (
  <div className='pdf-section'>
    <div style={{
      marginBottom: config.gap,
      fontFamily: config.fontFamily,
      background: config.sectionColor, // Fond de section avec section color
      padding: '20px',
      borderRadius: '8px',
      border: `1px solid ${config.sectionColor}40`
    }}>
      <div style={{
        fontSize: `calc(${config.fontSize || 18}px * 1.5)`,
        fontWeight: 'bold',
        color: config.primaryColor, // Titre de section en primary color
        marginBottom: 20,
        paddingBottom: 8,
        borderBottom: `2px solid ${config.primaryColor}`,
        textTransform: 'uppercase',
        letterSpacing: '1px',
        display: 'flex',
        alignItems: 'center',
        gap: 8
      }}>
        <User size={20} />
        {t('holderInformation')}
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: config.columns > 1 ? `repeat(${config.columns}, 1fr)` : '1fr',
        gap: 20
      }}>
        {/* Nom complet */}
        <InfoItem
          config={config}
          label={t('name')}
          value={`${data.last_name?.toUpperCase() || ''} ${data.first_name || ''} ${data.title ? `(${data.title})` : ''}`}
        />

        {/* Type et genre */}
        <InfoItem
          config={config}
          label={t('type')}
          value={`${t(data.type)} ${data.gender ? `(${t(data.gender === 'M' ? 'male' : 'female')})` : ''}`}
        />

        {/* Date de naissance */}
        <InfoItem
          config={config}
          label={t('birthDate')}
          value={`${formatDate ? formatDate(data.birth_date) : data.birth_date} ${data.age ? `(${data.age} ${t('years')})` : ''}`}
        />

        {/* Nationalité */}
        <InfoItem
          config={config}
          label={t('nationality')}
          value={data.nationality || t('notAvailable')}
        />

        {/* Passeport */}
        <InfoItem
          config={config}
          label={t('passport')}
          value={`${data.passport_number || t('notAvailable')} ${data.passport_country ? `(${data.passport_country})` : ''}`}
        />

        {/* Email */}
        {data.email && (
          <InfoItem
            config={config}
            label={t('email')}
            value={data.email}
          />
        )}

        {/* Téléphone */}
        {data.phone && (
          <InfoItem
            config={config}
            label={t('phone')}
            value={data.phone}
          />
        )}

        {/* Adresse */}
        {data.address && (
          <InfoItem
            config={config}
            label={t('address')}
            value={data.address}
          />
        )}
      </div>
    </div>
  </div>
);

export const PassengersSection = ({ data, config, t, formatDate }) => {

  // Style pour les cellules de l'entête
  const headerStyle = (config) => ({
    padding: '14px 16px',
    fontWeight: 700,
    fontSize: config.fontSize*0.9,
    color: config.textColor,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: `2px solid ${config.sectionColor}40`,
  });

  // Style pour les cellules du corps
  const cellStyle = (config, isLast) => ({
    padding: '12px 16px',
    fontSize: config.fontSize*0.9,
    color: config.textColor,
    // On n'affiche pas de bordure sur la dernière ligne pour ne pas doubler avec le conteneur
    borderBottom: isLast ? 'none' : `1px solid ${config.sectionColor}20`,
  });
  return (
    <div className='pdf-section' style={{
      background: 'transparent',
      marginBottom: config.gap
    }}>
      <div style={{
        marginBottom: 0,
        fontFamily: config.fontFamily,
        background: config.sectionColor, // ← Changé de backgroundColor à sectionColor
        padding: '20px',
        borderRadius: '8px',
        border: `1px solid ${config.sectionColor}40`
      }}>
        <div style={{
          fontSize: `calc(${config.fontSize || 18}px * 1.5)`,
          fontWeight: 'bold',
          color: config.primaryColor,
          marginBottom: 20,
          paddingBottom: 8,
          borderBottom: `2px solid ${config.primaryColor}`,
          textTransform: 'uppercase',
          letterSpacing: '1px',
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          <Users size={20} />
          {t('passengerInformation')}
        </div>

        <div style={{
          width: '100%',
          marginTop: '16px',
          borderRadius: '8px', // Coins arrondis ici
          border: `1px solid ${config.sectionColor}`, // Bordure légère autour
          overflow: 'hidden', // Important pour que le contenu ne dépasse pas des coins
          
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 0,
          }}>
            <thead>
              <tr style={{
                background: `${config.backgroundColor}`,
                textAlign: 'left',
              }}>
                <th style={headerStyle(config)}>{t('name')}</th>
                <th style={headerStyle(config)}>{t('gender')}</th>
                <th style={headerStyle(config)}>{t('type')}</th>
              </tr>
            </thead>
            <tbody>
              {data.passengers?.map((passenger, index) => (
                <tr key={index} style={{
                  background: `${config.backgroundColor}30`,
                  transition: 'background-color 0.2s'
                }}>
                  <td style={cellStyle(config, index === data.passengers.length - 1)}>{passenger.first_name} {passenger.last_name}</td>
                  <td style={cellStyle(config, index === data.passengers.length - 1)}>{passenger.gender}</td>
                  <td style={cellStyle(config, index === data.passengers.length - 1)}>{passenger.type}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export const ProviderInfoSection = ({ data, config, t }) => (
  <div className='pdf-section'>
    <div style={{
      marginBottom: config.gap,
      fontFamily: config.fontFamily,
      background: config.sectionColor, // Fond de section avec section color
      padding: '20px',
      borderRadius: '8px',
      border: `1px solid ${config.sectionColor}40`
    }}>
      <div style={{
        fontSize: `calc(${config.fontSize || 18}px * 1.5)`,
        fontWeight: 'bold',
        color: config.primaryColor, // Titre de section en primary color
        marginBottom: 20,
        paddingBottom: 8,
        borderBottom: `2px solid ${config.primaryColor}`,
        textTransform: 'uppercase',
        letterSpacing: '1px',
        display: 'flex',
        alignItems: 'center',
        gap: 8
      }}>
        <Shield size={20} />
        {data.name || t('providerInformation')}
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: config.columns > 1 ? `repeat(${config.columns}, 1fr)` : '1fr',
        gap: 20
      }}>
        {data.providerName && (
          <InfoItem
            config={config}
            label={t('providerName')}
            value={data.providerName}
            icon={<Building size={16} />}
          />
        )}

        {data.email && (
          <InfoItem
            config={config}
            label={t('email')}
            value={data.email}
            icon={<Mail size={16} />}
          />
        )}

        {data.phone && (
          <InfoItem
            config={config}
            label={t('phone')}
            value={data.phone}
            icon={<Phone size={16} />}
          />
        )}

        {data.address && (
          <InfoItem
            config={config}
            label={t('address')}
            value={data.address}
            icon={<MapPin size={16} />}
          />
        )}

        {data.website && (
          <InfoItem
            config={config}
            label={t('website')}
            value={data.website}
            icon={<Globe size={16} />}
          />
        )}

        {data.contactPerson && (
          <InfoItem
            config={config}
            label={t('contactPerson')}
            value={data.contactPerson}
            icon={<User size={16} />}
          />
        )}
      </div>
    </div>
  </div>
);

export const QrCodeSection = ({ data, config, t }) => {
  // Si l'utilisateur a uploadé un QR code personnalisé
  const hasCustomQrCode = data.customQrCodeUrl || data.qrCodeUrl;

  // URL du QR code (personnalisé ou généré)
  const qrCodeUrl = data.customQrCodeUrl || data.qrCodeUrl;

  // Texte à encoder dans le QR code
  const qrCodeText = data.qrCodeText ||
    `Booking: ${data.bookingNumber || ''}\n` +
    `Reference: ${data.reference || ''}\n` +
    `Status: ${data.status || ''}\n` +
    `Date: ${new Date().toLocaleDateString()}`;

  return (
    <div className='pdf-section'>
      <div style={{
        marginBottom: config.gap,
        fontFamily: config.fontFamily,
        background: config.sectionColor, // Fond de section avec section color
        padding: '20px',
        borderRadius: '8px',
        border: `1px solid ${config.sectionColor}40`
      }}>

        <div style={{
          fontSize: `calc(${config.fontSize || 18}px * 1.5)`,
          fontWeight: 'bold',
          color: config.primaryColor, // Titre de section en primary color
          marginBottom: 20,
          paddingBottom: 8,
          borderBottom: `2px solid ${config.primaryColor}`,
          textTransform: 'uppercase',
          letterSpacing: '1px',
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          <QrCode size={20} />
          {data.name || t('qrCode')}
        </div>


        {/* Conteneur principal du QR code */}
        <div style={{
          padding: '32px',
          background: config.sectionColor, // Fond interne avec background color
          borderRadius: config.borderRadius || '12px',
          border: data.borderStyle || `2px dashed ${config.secondaryColor}40`,
          boxShadow: data.showShadow ? '0 4px 6px rgba(0,0,0,0.1)' : 'none',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* QR code personnalisé uploadé */}
          {hasCustomQrCode ? (
            <>
              <div style={{
                width: data.size || 200,
                height: data.size || 200,
                margin: '0 auto 20px auto',
                position: 'relative',
                overflow: 'hidden',
                borderRadius: data.qrBorderRadius || '8px'
              }}>
                <img
                  src={qrCodeUrl}
                  alt={t('customQrCode') || "Custom QR Code"}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    filter: data.qrInverted ? 'invert(1)' : 'none'
                  }}
                />

                {/* Logo au centre du QR code */}
                {data.centerLogoUrl && (
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: data.logoSize || 40,
                    height: data.logoSize || 40,
                    background: 'white',
                    borderRadius: '8px',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <img
                      src={data.centerLogoUrl}
                      alt="Logo"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            // QR code par défaut (placeholder)
            <div style={{
              width: data.size || 200,
              height: data.size || 200,
              background: data.qrBackground || '#e5e7eb',
              borderRadius: data.qrBorderRadius || '8px',
              margin: '0 auto 20px auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: data.qrColor || '#6b7280',
              border: `2px dashed ${data.borderColor || '#d1d5db'}`,
              position: 'relative'
            }}>
              {data.showPlaceholder !== false ? (
                <>
                  <QrCode size={data.iconSize || 64} color={data.qrColor || "#9ca3af"} />
                  <div style={{
                    position: 'absolute',
                    bottom: 8,
                    fontSize: config.fontSize*0.7,
                    color: '#9ca3af',
                    textAlign: 'center',
                    width: '100%'
                  }}>
                    {t('qrCodePlaceholder')}
                  </div>
                </>
              ) : null}
            </div>
          )}

          {/* Texte sous le QR code */}
          <div style={{
            marginTop: 16,
            textAlign: 'center',
          }}>
            {data.title && (
              <div style={{
                fontSize: config.fontSize,
                fontWeight: 'bold',
                color: config.primaryColor, // Titre en primary color
                marginBottom: 8
              }}>
                {data.title}
              </div>
            )}

            <div style={{
              fontSize: data.textSize || 14,
              color: config.textColor || config.secondaryColor, // Texte en text color
              fontWeight: data.textWeight || 500,
              lineHeight: 1.6
            }}>
              {data.message || t('scanQRCode')}
            </div>

            {data.subMessage && (
              <div style={{
                fontSize: 12,
                color: config.secondaryColor, // Sous-message en secondary color
                marginTop: 8,
                fontStyle: 'italic'
              }}>
                {data.subMessage}
              </div>
            )}

            {/* Informations encodées (optionnel) */}
            {data.showEncodedInfo && (
              <div style={{
                fontSize: config.fontSize*0.7,
                color: config.secondaryColor, // Texte encodé en secondary color
                marginTop: 16,
                padding: '8px',
                background: 'rgba(0,0,0,0.03)',
                borderRadius: '4px',
                textAlign: 'left',
                maxHeight: 60,
                overflow: 'auto'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: 4, color: config.primaryColor }}>{t('encodedInformation')}:</div>
                <pre style={{
                  margin: 0,
                  fontSize: 9,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  color: config.textColor || '#1f2937'
                }}>
                  {qrCodeText}
                </pre>
              </div>
            )}
          </div>

          {/* Fond décoratif optionnel */}
          {data.decorativeBackground && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: data.decorativeBackground,
              opacity: 0.1,
              zIndex: 0
            }} />
          )}
        </div>

        {/* Instructions d'upload (seulement en mode édition) */}
        {data.isEditable && !hasCustomQrCode && (
          <div style={{
            fontSize: 12,
            color: config.secondaryColor, // Instructions en secondary color
            marginTop: 16,
            padding: '12px',
            background: `${config.backgroundColor}30`, // Fond avec background color
            borderRadius: '8px',
            textAlign: 'left',
            maxWidth: 400,
            margin: '16px auto 0'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: 4, color: config.primaryColor }}>{t('qrCodeInstructions')}:</div>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 11 }}>
              <li>{t('qrInstruction1')}</li>
              <li>{t('qrInstruction2')}</li>
              <li>{t('qrInstruction3')}</li>
              <li>{t('qrInstruction4')}</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export const ServicesSection = ({ data, config, t }) => (
  <div className='pdf-section'>
    <div style={{
      marginBottom: config.gap,
      fontFamily: config.fontFamily,
      background: config.sectionColor, // Fond de section avec section color
      padding: '20px',
      borderRadius: '8px',
      border: `1px solid ${config.sectionColor}40`
    }}>
      <div style={{
        fontSize: `calc(${config.fontSize || 18}px * 1.5)`,
        fontWeight: 'bold',
        color: config.primaryColor, // Titre de section en primary color
        marginBottom: 20,
        paddingBottom: 8,
        borderBottom: `2px solid ${config.primaryColor}`,
        textTransform: 'uppercase',
        letterSpacing: '1px',
        display: 'flex',
        alignItems: 'center',
        gap: 8
      }}>
        <ListCheck size={20} />
        {t('servicesInclusionsExclusions')}
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: config.columns > 1 ? `repeat(${config.columns}, 1fr)` : '1fr',
        gap: 20
      }}>
        {/* Included Services */}
        {data.extra_includes && data.extra_includes.length > 0 && (
          <div style={{
            padding: '16px',
            background: `${config.backgroundColor}30`, // Fond interne avec background color
            borderRadius: '8px',
            border: `1px solid ${config.primaryColor}20`
          }}>
            <div style={{
              fontSize: config.fontSize,
              fontWeight: 600,
              marginBottom: 12,
              color: config.primaryColor, // Titre "Inclus" en primary color
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              <ListCheck size={16} />
              {t('included')}
            </div>
            <div style={{
              fontSize: config.fontSize,
              color: config.textColor || '#374151', // Texte en text color
              lineHeight: 1.6
            }}>
              {data.extra_includes.map((service, index) => (
                <div key={index} style={{ marginBottom: 8 }}>
                  • {service.value}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Excluded Services */}
        {data.extra_excludes && data.extra_excludes.length > 0 && (
          <div style={{
            padding: '16px',
            background: `${config.backgroundColor}30`, // Fond interne avec background color
            borderRadius: '8px',
            border: `1px solid #ef444420`
          }}>
            <div style={{
              fontSize: config.fontSize,
              fontWeight: 600,
              marginBottom: 12,
              color: '#ef4444', // Garder la couleur rouge pour "Exclu"
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              <ListCheck size={16} />
              {t('excluded')}
            </div>
            <div style={{
              fontSize: config.fontSize,
              color: config.textColor || '#374151', // Texte en text color
              lineHeight: 1.6
            }}>
              {data.extra_excludes.map((service, index) => (
                <div key={index} style={{ marginBottom: 8 }}>
                  • {service.value}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
);

export const CancellationPoliciesSection = ({ data, config, t, formatDateTime }) => (
  <div className='pdf-section'>
    <div style={{
      marginBottom: config.gap,
      fontFamily: config.fontFamily,
      background: config.sectionColor, // Fond de section avec section color
      padding: '20px',
      borderRadius: '8px',
      border: `1px solid ${config.sectionColor}40`
    }}>
      <div style={{
        fontSize: `calc(${config.fontSize || 18}px * 1.5)`,
        fontWeight: 'bold',
        color: config.primaryColor, // Titre de section en primary color
        marginBottom: 20,
        paddingBottom: 8,
        borderBottom: `2px solid ${config.primaryColor}`,
        textTransform: 'uppercase',
        letterSpacing: '1px',
        display: 'flex',
        alignItems: 'center',
        gap: 8
      }}>
        <ScrollText size={20} />
        {t('cancellationPolicies')}
      </div>

      {data.cancellation_policies?.map((policy, index) => (
        <div key={index} style={{
          marginBottom: 16,
          padding: '16px',
          background: config.sectionColor, // Fond interne avec background color
          borderRadius: '8px',
          border: `1px solid ${config.sectionColor}20`
        }}>
 <div style={{
        display: 'grid',
        gridTemplateColumns: config.columns > 1 ? `repeat(${config.columns}, 1fr)` : '1fr',
        gap: 20
      }}>
            <InfoItem
              config={config}
              label={t('deadline')}
              value={formatDateTime(policy.date)}
              icon={<Calendar size={16} />}
            />

            <InfoItem
              config={config}
              label={t('fees')}
              value={`${policy.amount} ${data.currency}`}
              icon={<Wallet size={16} />}
            />

            <InfoItem
              config={config}
              label={t('providerFees')}
              value={`${policy.amountProvider} ${data.provider_currency}`}
              icon={<Shield size={16} />}
            />
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const ContactInformationSection = ({ data, config, t }) => (
  <div className='pdf-section'>
    <div style={{
      marginBottom: config.gap,
      fontFamily: config.fontFamily,
      background: config.sectionColor, // Fond de section avec section color
      padding: '20px',
      borderRadius: '8px',
      border: `1px solid ${config.sectionColor}40`
    }}>
      <div style={{
        fontSize: `calc(${config.fontSize || 18}px * 1.5)`,
        fontWeight: 'bold',
        color: config.primaryColor, // Titre de section en primary color
        marginBottom: 20,
        paddingBottom: 8,
        borderBottom: `2px solid ${config.primaryColor}`,
        textTransform: 'uppercase',
        letterSpacing: '1px',
        display: 'flex',
        alignItems: 'center',
        gap: 8
      }}>
        <User size={20} />
        {t('contactInformation')}
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: config.columns > 1 ? `repeat(${config.columns}, 1fr)` : '1fr',
        gap: 20
      }}>
        {/* Section Produit */}
        {data.product && (
          <div style={{
            padding: '16px',
            background: `${config.backgroundColor}30`,
            borderRadius: '8px',
            border: `2px solid ${config.backgroundColor}`
          }}>
            <div style={{
              fontSize: config.fontSize,
              fontWeight: 600,
              marginBottom: 12,
              textAlign: 'center',
              color: config.primaryColor // Titre en primary color
            }}>
              {t('productInfo')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {data.product.name && (
                <InfoItem
                  config={config}
                  label={t('product')}
                  value={data.product.name}
                />
              )}
              {data.product.phone && (
                <InfoItem
                  config={config}
                  label={t('phone')}
                  value={data.product.phone}
                  icon={<Phone size={14} />}
                />
              )}
              {data.product.address && (
                <InfoItem
                  config={config}
                  label={t('address')}
                  value={data.product.address}
                  icon={<MapPin size={14} />}
                />
              )}
            </div>
          </div>
        )}

        {/* Section Client */}
        {data.customer && (
          <div style={{
            padding: '16px',
            background: `${config.backgroundColor}30`, // Fond interne avec background color
            borderRadius: '8px',
            border: `2px solid ${config.backgroundColor}`
          }}>
            <div style={{
              fontSize: config.fontSize,
              fontWeight: 600,
              marginBottom: 12,
              textAlign: 'center',
              color: config.primaryColor // Titre en primary color
            }}>
              {t('customer')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {data.customer.name && (
                <InfoItem
                  config={config}
                  label={t('customer')}
                  value={data.customer.name}
                />
              )}
              {data.customer.phone && (
                <InfoItem
                  config={config}
                  label={t('phone')}
                  value={data.customer.phone}
                  icon={<Phone size={14} />}
                />
              )}
              {data.customer.address && (
                <InfoItem
                  config={config}
                  label={t('address')}
                  value={data.customer.address}
                  icon={<MapPin size={14} />}
                />
              )}
            </div>
          </div>
        )}

        {/* Section Fournisseur */}
        {data.provider && (
          <div style={{
            padding: '16px',
            background: `${config.backgroundColor}30`, // Fond interne avec background color
            borderRadius: '8px',
            border: `2px solid ${config.backgroundColor}`
          }}>
            <div style={{
              fontSize: config.fontSize,
              fontWeight: 600,
              marginBottom: 12,
              textAlign: 'center',
              color: config.primaryColor // Titre en primary color
            }}>
              {t('provider')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {(data.provider.name || data.provider.email) && (
                <InfoItem
                  config={config}
                  label={t('provider')}
                  value={data.provider?.name || data.provider?.email}
                />
              )}
              {data.provider.phone && (
                <InfoItem
                  config={config}
                  label={t('phone')}
                  value={data.provider.phone}
                  icon={<Phone size={14} />}
                />
              )}
              {data.provider.address && (
                <InfoItem
                  config={config}
                  label={t('address')}
                  value={data.provider.address}
                  icon={<MapPin size={14} />}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
);

export const NotesSection = ({ data, config, t }) => {
  const notesConfig = config?.notesConfig || {};

  const notes = notesConfig.notes && notesConfig.notes.length > 0
    ? notesConfig.notes
    : (data.notes || []);
  const filteredNotes = notes.filter(note => note && note.trim() !== '');

  if (filteredNotes.length === 0) {
    return null;
  }

  return (
    <div className='pdf-section'>
      <div style={{
        marginBottom: config.gap,
        fontFamily: config.fontFamily,
        background: config.sectionColor, // Fond de section avec section color
        padding: '20px',
        borderRadius: '8px',
        border: `1px solid ${config.sectionColor}40`
      }}>
        <div style={{
          fontSize: `calc(${config.fontSize || 18}px * 1.5)`,
          fontWeight: 'bold',
          color: config.primaryColor, // Titre de section en primary color
          marginBottom: 20,
          paddingBottom: 8,
          borderBottom: `2px solid ${config.primaryColor}`,
          textTransform: 'uppercase',
          letterSpacing: '1px',
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          <FileExclamationPoint size={20} />
          {data.name || t('importantNotes')}
        </div>

        <div style={{
          background: `${config.backgroundColor}30`, // Fond interne avec background color
          padding: '20px',
          borderRadius: '8px',
          borderLeft: `4px solid ${config.primaryColor}`
        }}>
          {filteredNotes.map((note, index) => (
            <div key={index} style={{
              marginBottom: '8px',
              fontSize: config.fontSize*0.9,
              color: config.textColor || '#374151', // Notes en text color
              lineHeight: '1.6',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px'
            }}>
              <span style={{ color: config.primaryColor, fontWeight: 'bold' }}>•</span>
              <span>{note}</span>
            </div>
          ))}

          {notesConfig.showGenerationDate && data.generationDate && (
            <div style={{
              marginTop: '16px',
              fontSize: config.fontSize*0.9,
              color: config.secondaryColor, // Date en secondary color
              fontStyle: 'italic',
              borderTop: `1px solid ${config.sectionColor}20`
            }}>
              {t('voucherGeneratedOn')}: {data.generationDate}
            </div>
          )}

          {notesConfig.emergencyContact && (
            <div style={{
              marginTop: '16px',
              fontSize: config.fontSize*0.9,
              color: '#dc2626', // Garder le rouge pour le contact d'urgence
              fontWeight: '600'
            }}>
              {t('emergencyContact')}: {notesConfig.emergencyContact}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const FooterSection = ({ data, config, t, logoConfig }) => {
  const hasLogo = logoConfig?.footer?.enabled && logoConfig?.logoUrl;
  const position = logoConfig?.footer?.position || 'center';

  return (
    <div className='pdf-section'>
      <div style={{
        marginTop: config.gap,
        position: 'relative',
        background: config.sectionColor, // Fond du footer avec background color
        padding: '20px',
        borderRadius: '8px',
        borderTop: `1px solid ${config.sectionColor}20`
      }}>
        <div style={{
          
          paddingTop: '24px',
          textAlign: position,
          fontSize: config.fontSize*0.9,
          color: config.secondaryColor, // Texte du footer en secondary color
          lineHeight: '1.6',
          display: 'flex',
          flexDirection: position === 'left' || position === 'right' ? 'row' : 'column',
          alignItems: 'center',
          justifyContent: position === 'left' ? 'flex-start' :
            position === 'right' ? 'flex-end' : 'center',
          gap: position === 'left' || position === 'right' ? '20px' : '8px'
        }}>
          {hasLogo && (position === 'left' || position === 'right') && (
            <img
              src={logoConfig.logoUrl}
              alt={t('companyLogo') || 'Logo'}
              style={{
                width: LOGO_SIZES.find(s => s.value === logoConfig.footer?.size)?.size || 50,
                height: 'auto',
                maxWidth: '100%',
                order: position === 'right' ? 2 : 0
              }}
            />
          )}

          <div style={{
            flex: position === 'left' || position === 'right' ? 1 : 'auto',
            textAlign: 'center'
          }}>
            {hasLogo && position === 'center' && (
              <div style={{ marginBottom: 8 }}>
                <img
                  src={logoConfig.logoUrl}
                  alt={t('companyLogo') || 'Logo'}
                  style={{
                    width: LOGO_SIZES.find(s => s.value === logoConfig.footer?.size)?.size || 50,
                    height: 'auto',
                    maxWidth: '100%'
                  }}
                />
              </div>
            )}

            <p style={{ margin: '0 0 8px 0', color: config.textColor || '#1f2937' }}>
              {data.message || t('officialVoucherMessage')}
            </p>
            <p style={{ margin: 0, color: config.textColor || '#1f2937' }}>
              {t('generatedBy')}: {data.generatedBy || 'IDH EXTRANET System'}
            </p>
            {data.copyright && (
              <p style={{ margin: '8px 0 0 0', fontSize: '11px', color: config.secondaryColor }}>
                © {new Date().getFullYear()} {data.copyright}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// conteneur principal qui inclut logo et filigrane
export const VoucherContainer = ({ children, config, logoConfig, setLogoConfig, watermarkConfig, t, pageNumber, totalPages }) => {

  const [dragState, setDragState] = useState(null);

  const handleMouseDown = (e) => {
    e.preventDefault();

    setDragState({
      startX: e.clientX,
      startY: e.clientY,
      logoX: logoConfig.customization.x,
      logoY: logoConfig.customization.y
    });
  };

  const handleMouseUp = () => {
    setDragState(null);
  };

  const handleMouseMove = (e) => {
    if (!dragState) return;

    const deltaX = e.clientX - dragState.startX;
    const deltaY = e.clientY - dragState.startY;

    setLogoConfig(prev => ({
      ...prev,
      customization: {
        ...prev.customization,
        x: dragState.logoX + deltaX,
        y: dragState.logoY + deltaY
      }
    }));
  };

  const getWatermarkStyle = () => {
    const position = watermarkConfig?.watermarkPosition || 'center';
    const opacity = (watermarkConfig?.watermarkOpacity || 15) / 100;
    const rotation = watermarkConfig?.watermarkRotation || -45;

    if (position === 'repeat') {
      return {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 999,
        opacity,
        overflow: 'hidden'
      };
    }

    const positionStyle = {
      'center': {
        top: '50%',
        left: '50%',
        transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
        textAlign: 'center'
      },
      'diagonal': {
        top: '20%',
        left: '10%',
        transform: `rotate(${rotation}deg)`
      },
      'corner': {
        bottom: '10%',
        right: '10%',
        transform: `rotate(${rotation}deg)`
      }
    };

    return {
      position: 'absolute',
      pointerEvents: 'none',
      zIndex: 999,
      backgroundRepeat: 'repeat',
      opacity,
      ...positionStyle[position]
    };
  };

  // Calcul de la taille du filigrane
  const getWatermarkSize = () => {
    const size = watermarkConfig?.watermarkSize || 'large';
    const sizeMap = {
      'small': { text: 64, image: 250 },
      'medium': { text: 96, image: 400 },
      'large': { text: 128, image: 600 },
      'xlarge': { text: 160, image: 800 }
    };
    return sizeMap[size] || sizeMap.large;
  };

  return (
    <div className='pdf-container'>
      <div
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{
          position: 'relative',
          background: config.backgroundColor || '#ffffff', // Fond principal avec background color
          borderRadius: config.borderRadius || 12,
          padding: config.padding || 40,
          minHeight: '100vh',
          fontFamily: config.fontFamily || "'Inter', sans-serif",
          fontSize: config.fontSize || 16,
          color: config.textColor || '#1f2937', // Texte principal en text color
          overflow: 'hidden'
        }}>

        {logoConfig?.customization?.enabled && logoConfig?.logoUrl && (
          <div
            style={{
              position: 'absolute',
              left: logoConfig.customization.x,
              top: logoConfig.customization.y,
              zIndex: 10,
              cursor: 'grab',
              opacity: logoConfig.customization.opacity / 100
            }}
            onMouseDown={handleMouseDown}
          >
            <img
              src={logoConfig.logoUrl}
              alt="Logo"
              draggable={false}
              style={{
                width: logoConfig.customization.width,
                height: 'auto',
                pointerEvents: 'none'
              }}
            />
          </div>
        )}

        {/* Filigrane */}
        {watermarkConfig?.showWatermark && (
          <>
            {watermarkConfig.watermarkPosition === 'repeat' ? (
              // Filigrane répété
              <div style={getWatermarkStyle()}>
                {watermarkConfig.watermarkType === 'text' && watermarkConfig.watermarkText ? (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: '100px',
                    padding: '50px'
                  }}>
                    {Array.from({ length: 12 }).map((_, i) => (
                      <div
                        key={i}
                        style={{
                          fontSize: getWatermarkSize().text,
                          fontWeight: 'bold',
                          color: config.secondaryColor + '20', // Filigrane avec secondary color transparent
                          textAlign: 'center',
                          transform: `rotate(${watermarkConfig.watermarkRotation}deg)`,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {watermarkConfig.watermarkText}
                      </div>
                    ))}
                  </div>
                ) : watermarkConfig.watermarkType === 'image' && watermarkConfig.watermarkUrl ? (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '50px',
                    padding: '30px'
                  }}>
                    {Array.from({ length: 20 }).map((_, i) => (
                      <img
                        key={i}
                        src={watermarkConfig.watermarkUrl}
                        alt={t('watermark') || 'Watermark'}
                        style={{
                          width: '100%',
                          height: 'auto',
                          opacity: watermarkConfig.watermarkOpacity / 100,
                          transform: `rotate(${watermarkConfig.watermarkRotation}deg)`
                        }}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            ) : (
              // Filigrane unique
              <div style={getWatermarkStyle()}>
                {watermarkConfig.watermarkType === 'text' && watermarkConfig.watermarkText ? (
                  <div style={{
                    fontSize: getWatermarkSize().text,
                    fontWeight: 'bold',
                    color: config.secondaryColor + '20', // Filigrane avec secondary color transparent
                    whiteSpace: 'nowrap'
                  }}>
                    {watermarkConfig.watermarkText}
                  </div>
                ) : watermarkConfig.watermarkType === 'image' && watermarkConfig.watermarkUrl ? (
                  <img
                    src={watermarkConfig.watermarkUrl}
                    alt={t('watermark') || 'Watermark'}
                    style={{
                      width: getWatermarkSize().image,
                      height: 'auto',
                      opacity: watermarkConfig.watermarkOpacity / 100
                    }}
                  />
                ) : null}
              </div>
            )}
          </>
        )}

        {/* Contenu principal */}
        <div style={{
          position: 'relative',
          zIndex: 2
        }}>
          {children}
        </div>
      </div>
    </div>
  );
};

// Ajoutez ces constantes en haut du fichier
const LOGO_SIZES = [
  { value: 'small', size: 50 },
  { value: 'medium', size: 100 },
  { value: 'large', size: 150 }
];

// Mettre à jour le mapping SectionComponents pour inclure tous les composants
export const SectionComponents = {
  HeaderSection,
  BookingInfoSection,
  HolderSection,
  ProviderInfoSection,
  PassengersSection,
  QrCodeSection,
  ServicesSection,
  CancellationPoliciesSection,
  ContactInformationSection,
  NotesSection,
  FooterSection
};

// Fonction utilitaire pour obtenir un composant par nom
export const getSectionComponent = (componentName) => {
  return SectionComponents[componentName] || null;
};